"use client";

import * as React from "react";
import {
  AppBar,
  Box,
  Container,
  CssBaseline,
  Divider,
  IconButton,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Link as MUILink,
  Chip,
  Paper,
  Stack,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import { ThemeProvider, createTheme } from "@mui/material/styles";

// Material Design 3 (M3) style theme
const theme = createTheme({
  palette: {
    primary: { main: "#6750A4" },
    secondary: { main: "#625B71" },
    background: { default: "#FFFBFE" },
  },
  shape: { borderRadius: 16 },
  typography: {
    fontFamily: [
      "Inter",
      "system-ui",
      "-apple-system",
      "Segoe UI",
      "Roboto",
      "Ubuntu",
      "Cantarell",
      "Noto Sans",
      "Helvetica Neue",
      "Arial",
      "sans-serif",
    ].join(","),
    h1: { fontWeight: 700, letterSpacing: -0.5 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
  },
});

function Section({ title, children, defaultExpanded = false }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} elevation={0} sx={{
      bgcolor: "transparent",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
    }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.25}>{children}</Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function PrivacyPolicyPage() {
  const [effectiveDate] = React.useState("August 25, 2025");

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="sticky" color="primary" enableColorOnDark>
        <Toolbar>
          {/* <IconButton size="large" edge="start" color="inherit" aria-label="menu" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton> */}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Poster Guru — Privacy Policy
          </Typography>
          <Button href="#contact" variant="outlined" color="inherit">
            Contact
          </Button>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ py: { xs: 4, md: 8 } }}>
        <Container maxWidth="md">
          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: "1px solid", borderColor: "divider" }}>
            <Stack spacing={3}>
              <Box>
                <Chip label={`Effective Date: ${effectiveDate}`} color="secondary" variant="outlined" sx={{ mb: 1 }} />
                <Typography variant="h3" gutterBottom>
                  Privacy Policy
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Welcome to <strong>Poster Guru</strong> ("we", "our", "us"). Your privacy matters to us. This Privacy Policy explains what information we collect, how we use it, how we protect it, and the choices you have. By using Poster Guru, you agree to the practices described here.
                </Typography>
              </Box>

              <Divider />

              <Section title="1. Information We Collect" defaultExpanded>
                <Typography>
                  We collect the minimum data necessary to deliver and improve Poster Guru. The information may include:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Personal Information"
                      secondary="Name, email address, or account login details (if you sign up). Payment details are processed by trusted payment providers; we never store full card numbers."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Non-Personal Information"
                      secondary="Device model, OS version, app version, usage analytics, crash logs, performance data, and (if enabled) approximate location for localized templates."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="User-Generated Content"
                      secondary="Posters, images, logos, and text you create or upload. You retain ownership; we process them only to provide features like editing, export, and cloud backup."
                    />
                  </ListItem>
                </List>
              </Section>

              <Section title="2. How We Use Your Information" defaultExpanded>
                <List dense>
                  <ListItem><ListItemText primary="Provide core functionality" secondary="Create, edit, export, and share your designs; sync content across devices (if enabled)." /></ListItem>
                  <ListItem><ListItemText primary="Improve app quality" secondary="Diagnose crashes, enhance performance, and guide UX decisions with aggregate analytics." /></ListItem>
                  <ListItem><ListItemText primary="Personalize content" secondary="Recommend templates, fonts, and color palettes based on usage (optional)." /></ListItem>
                  <ListItem><ListItemText primary="Payments & subscriptions" secondary="Activate premium features via secure third‑party processors (e.g., Play Store, App Store, Stripe)." /></ListItem>
                  <ListItem><ListItemText primary="Security & fraud prevention" secondary="Protect accounts, investigate abuse, and enforce policies." /></ListItem>
                  <ListItem><ListItemText primary="Communications" secondary="Send service updates and, if you opt in, tips and promotions. You can unsubscribe anytime." /></ListItem>
                </List>
              </Section>

              <Section title="3. Sharing & Disclosure">
                <Typography>
                  We do <strong>not</strong> sell your personal data. We may share limited data with:
                </Typography>
                <List dense>
                  <ListItem><ListItemText primary="Service providers" secondary="Analytics, cloud storage, customer support, and payment processing strictly under contract." /></ListItem>
                  <ListItem><ListItemText primary="Legal compliance" secondary="When required by law or to protect rights, safety, and integrity of our services." /></ListItem>
                  <ListItem><ListItemText primary="Business transfers" secondary="In mergers or acquisitions, data may be transferred under the same privacy commitments." /></ListItem>
                </List>
              </Section>

              <Section title="4. Data Security">
                <Typography>
                  We apply administrative, technical, and physical safeguards including encryption in transit, access controls, and routine security reviews. No method is 100% secure; we strive to protect your data continuously.
                </Typography>
              </Section>

              <Section title="5. Data Retention">
                <Typography>
                  We keep personal data only as long as needed to provide Poster Guru and meet legal obligations. You can request deletion of your account and associated personal data. Certain de‑identified analytics may be retained.
                </Typography>
              </Section>

              <Section title="6. Children’s Privacy">
                <Typography>
                  Poster Guru is not intended for children under 13. We do not knowingly collect data from children. If you believe a child provided personal data, contact us to remove it.
                </Typography>
              </Section>

              <Section title="7. Your Rights & Choices">
                <List dense>
                  <ListItem><ListItemText primary="Access & correction" secondary="Request a copy of your data and correct inaccuracies." /></ListItem>
                  <ListItem><ListItemText primary="Deletion" secondary="Ask us to delete your personal data, subject to legal allowances." /></ListItem>
                  <ListItem><ListItemText primary="Consent management" secondary="Enable/disable analytics, personalized content, and marketing within settings." /></ListItem>
                  <ListItem><ListItemText primary="Opt‑out of marketing" secondary="Use in‑app controls or the unsubscribe link in emails." /></ListItem>
                </List>
              </Section>

              <Section title="8. Third‑Party Services & SDKs">
                <Typography>
                  Poster Guru may integrate third‑party services (e.g., Firebase/Analytics, AdMob, Stripe/Play Billing). Their use of information is governed by their own privacy policies. We recommend reviewing those policies.
                </Typography>
              </Section>

              <Section title="9. Cookies & Similar Technologies">
                <Typography>
                  We may use cookies, local storage, and similar technologies to remember preferences, maintain sessions, and analyze usage. You can control these via device or in‑app settings; some features may be limited if disabled.
                </Typography>
              </Section>

              <Section title="10. International Data Transfers">
                <Typography>
                  Your information may be processed and stored on servers outside your country. We use appropriate safeguards for cross‑border transfers where required by law.
                </Typography>
              </Section>

              <Section title="11. Changes to This Policy">
                <Typography>
                  We may update this Privacy Policy from time to time. We will post the updated version in the app and adjust the effective date above. Material changes will be communicated via in‑app notice or email.
                </Typography>
              </Section>


              <Section title="12. Refund/return">
                <Typography>
                  We have a no Refund/return policy, As the products are digital posters.
                </Typography>
              </Section>


              <Section title="13. Shipping">
                <Typography>
                  We do not offer shipping services because we do not sell any physical products.
                  All our offerings are digital services that are delivered electronically.
                  Once your order is confirmed, you will receive access to our services via email, user dashboard, or other digital delivery methods as specified.
                  If you have any questions about accessing your purchased service, please feel free to contact us at infomagnisys@gmail.com
                </Typography>
              </Section>


              <Section title="14. Pricing">
                <List dense>
                  <ListItem>
                    <ListItemText primary="You can use Poster Guru by subscribing to one of our plans:"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText secondary="₹149 for 1 month" />
                  </ListItem>
                  <ListItem>
                    <ListItemText secondary="₹349 for 3 months" />
                  </ListItem>
                  <ListItem>
                    <ListItemText secondary="₹699 for 1 year" />
                  </ListItem>
                </List>
              </Section>


              <Section title="15. Terms and Conditions">
                Welcome to Poster Guru ("we", "our", "us"). By accessing or using our platform, you agree to be bound by these Terms and Conditions, as well as our Privacy Policy. Poster Guru provides tools and templates to help users create visual content such as posters and graphics. All content, features, and materials made available through our platform are owned by us or our licensors and are protected by intellectual property laws.
                You may use Poster Guru for personal or business purposes, but you may not copy, redistribute, or misuse any part of our service. When you create and upload content, you retain ownership of it, but you grant us limited rights to store and display it as necessary to provide our services to you.
                We care about your privacy. Our Privacy Policy explains what data we collect, how we use it, and how we protect it. By using Poster Guru, you consent to these practices.
                We may update these terms from time to time. Your continued use of Poster Guru after changes means you accept the updated terms. If you have any questions or concerns, feel free to contact us.
              </Section>


              <Section title="16. Contact Us" defaultExpanded>
                <Typography id="contact">
                  If you have questions or requests regarding privacy, contact us: JPK Enterprise
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Email"
                      secondary={<MUILink href="mailto:erkrkanhaiya@gmail.com">erkrkanhaiya@gmail.com</MUILink>}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Phone"
                      secondary='+91 95297 41079'
                    />
                  </ListItem>
                  {/* <ListItem>
                    <ListItemText primary="Website" secondary={<MUILink href="https://www.posterguru.app" target="_blank" rel="noreferrer">www.posterguru.app</MUILink>} />
                  </ListItem> */}
                  <ListItem>
                    <ListItemText primary="Address" secondary="Opp.BIADA main Gate Sugar factory raod Banmankhi Bazar, Purnia 854202" />
                  </ListItem>
                </List>
              </Section>

              <Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary" display="block">
                  ✨ Poster Guru – Turn your ideas into posters that inspire, engage, and leave a lasting impression.
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  fontWeight={700}
                  marginTop={2}
                  fontSize={18}
                >
                     ✨ Poster Guru is powered by JPK Enterprise. All rights reserved © JPK Enterprise.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
